// Mind Matters Website | Rupali | Version 1.3 | April 2026
import { useEffect, useRef, useState, createContext, useContext } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import "@/App.css";
import {
  Heart,
  Shield,
  Star,
  Menu,
  X,
  Mail,
  IndianRupee,
  Pencil,
  Phone,
  Info,
  Lock,
  ClipboardList,
  MessageCircle,
  Instagram,
  Youtube,
  ChevronUp,
  PlayCircle,
  Users,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  CalendarDays,
  Clock,
  CreditCard,
  Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URLS = {
  instagram: "https://www.instagram.com/mind_matters_rupali",
  youtube: "https://www.youtube.com/@mind_matters_rupali",
  terms: "/Mind_Matters_Terms_Conditions.pdf",
  questionnaire:
    "https://docs.google.com/forms/d/e/1FAIpQLSdCrY7L8gtIweAx1kgEEbikYpba6odFu3aIaul2AHrbhOJ3sw/viewform",
};

/* =========================================================
   NAVIGATION CONTEXT (page state)
   ========================================================= */
const NavCtx = createContext({ go: () => {}, goBook: () => {}, goAnchor: () => {} });
const useNav = () => useContext(NavCtx);

const NAV_LINKS = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#how-it-works", label: "How It Works" },
  { href: "#testimonials", label: "Testimonials" },
];

/* =========================================================
   NAVIGATION BAR
   ========================================================= */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { goBook, go, goAnchor } = useNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLink = (e, href) => {
    e.preventDefault();
    goAnchor(href);
  };

  return (
    <>
      <nav className={`mm-nav ${scrolled ? "scrolled" : ""}`} data-testid="main-nav">
        <div className="mm-nav-inner">
          <button
            onClick={() => go("home")}
            className="mm-logo"
            style={{ background: "none", border: "none", cursor: "pointer" }}
            data-testid="nav-logo"
          >
            Mind Matters
          </button>
          <ul className="mm-nav-links">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={(e) => handleLink(e, l.href)}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <button
                onClick={() => goBook()}
                className="btn btn-primary btn-sm"
                data-testid="nav-book-btn"
              >
                Book a Session
              </button>
            </li>
          </ul>
          <button
            className="mm-mobile-toggle"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            <Menu size={26} />
          </button>
        </div>
      </nav>

      <div className={`mm-mobile-overlay ${open ? "open" : ""}`} data-testid="mobile-menu">
        <button
          onClick={() => setOpen(false)}
          aria-label="Close menu"
          style={{ alignSelf: "flex-end", background: "none", border: "none", color: "#2D1B20", cursor: "pointer" }}
          data-testid="mobile-menu-close"
        >
          <X size={28} />
        </button>
        <ul>
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                onClick={(e) => { setOpen(false); handleLink(e, l.href); }}
              >
                {l.label}
              </a>
            </li>
          ))}
          <li>
            <button
              onClick={() => { setOpen(false); goBook(); }}
              className="btn btn-primary"
              style={{ marginTop: 12 }}
            >
              Book a Session
            </button>
          </li>
        </ul>
      </div>
    </>
  );
};

/* =========================================================
   HERO
   ========================================================= */
const Hero = () => {
  const { goBook, goAnchor } = useNav();
  return (
    <section id="home" className="mm-hero">
      <div className="blob blob-1" />
      <div className="blob blob-2" />
      <div className="container hero-grid">
        <div className="reveal">
          <div className="hero-socials">
            <a href={URLS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="hero-instagram">
              <Instagram size={16} /> <span>@mind_matters_rupali</span>
            </a>
            <a href={URLS.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="hero-youtube">
              <Youtube size={16} /> <span>YouTube</span>
            </a>
          </div>
          <h1 className="hero-title">
            Healing Begins<br />
            at <em>Mind Matters</em>
          </h1>
          <div className="hero-ctas" style={{ marginTop: 32 }}>
            <button onClick={() => goBook()} className="btn btn-primary" data-testid="hero-book-btn">Book a Session</button>
            <a href="#services" className="btn btn-outline" data-testid="hero-services-btn">View Services</a>
          </div>
          <div className="hero-badges">
            <div className="hero-badge">
              <div className="icon-circle"><Heart size={18} fill="currentColor" /></div>
              <span>4+ Years Experience</span>
            </div>
            <div className="hero-badge">
              <div className="icon-circle"><Shield size={18} /></div>
              <span>200+ Sessions</span>
            </div>
            <div className="hero-badge">
              <div className="icon-circle"><Star size={18} fill="currentColor" /></div>
              <span>Confidential &amp; Safe</span>
            </div>
          </div>
        </div>

        <div className="reveal">
          <div className="hero-photo-wrap">
            <div className="hero-photo" data-testid="hero-photo">
              <img src="/rupali_hero.jpeg" alt="Rupali — Mind Matters" />
            </div>
            <div className="floating-card fc-1">
              <span className="dot-pink"><PlayCircle size={16} /></span>
              <div>
                <div style={{ fontWeight: 600 }}>32M+ Views</div>
                <div style={{ fontSize: 11, color: "#7A5560", fontWeight: 400 }}>Across platforms</div>
              </div>
            </div>
            <div className="floating-card fc-2">
              <span className="dot-pink"><Star size={16} fill="#E8738A" /></span>
              <div>
                <div style={{ fontWeight: 600 }}>4.9 Rating</div>
                <div style={{ fontSize: 11, color: "#7A5560", fontWeight: 400 }}>Client reviews</div>
              </div>
            </div>
            <div className="floating-card fc-3">
              <span className="dot-pink"><Users size={16} /></span>
              <div>
                <div style={{ fontWeight: 600 }}>200+ Clients</div>
                <div style={{ fontSize: 11, color: "#7A5560", fontWeight: 400 }}>Trusted &amp; served</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* =========================================================
   ABOUT
   ========================================================= */
const About = () => (
  <section id="about" className="section">
    <div className="container about-grid">
      <div className="reveal about-photo-col">
        <div className="about-photo" data-testid="about-photo">
          <img src="/rupali_about_me.png" alt="Rupali — psychologist" />
        </div>
      </div>
      <div className="reveal">
        <div className="about-kicker">About Rupali</div>
        <h2 className="about-title">The mind behind <em style={{ color: "var(--mm-accent)", fontStyle: "italic" }}>Mind Matters</em></h2>
        <p className="about-sub">A psychologist, a counsellor, and a guide for the heart.</p>
        <div className="about-text">
          <p>
            Hi, I'm Rupali — a psychologist, relationship counsellor, and the creator of
            Mind Matters. I hold a Masters in Psychology, and over the past four
            years I have had the privilege of working with hundreds of individuals and couples
            navigating some of the most personal challenges of their lives.
          </p>
          <p>
            My practice focuses on relationship psychology — the patterns, emotions, and dynamics
            that shape how we connect with the people we love. I work with concerns ranging from
            communication breakdowns and marital conflict to attachment patterns, pre-marital
            counselling, and family dynamics. My approach is warm, non-judgmental, and deeply
            personalised.
          </p>
          <p>
            Through Mind Matters on Instagram and YouTube, I have reached over 27 lakh people every
            month — creating conversations around mental health, relationships, and emotional
            wellbeing in the Indian context. Now, I bring that same insight and care directly to
            you, one conversation at a time.
          </p>
        </div>
        <div className="cred-grid">
          <div className="cred-box"><h4>Masters in Psychology</h4><p>Master's Degree Qualified</p></div>
          <div className="cred-box"><h4>4+ Years</h4><p>Hands-On Counselling Experience</p></div>
          <div className="cred-box"><h4>27L+ Monthly Views</h4><p>Mind Matters on Instagram &amp; YouTube</p></div>
          <div className="cred-box"><h4>Hindi &amp; English</h4><p>Sessions available in both languages</p></div>
        </div>
      </div>
    </div>
  </section>
);

/* =========================================================
   SERVICES — central data source
   ========================================================= */
const SERVICES = [
  {
    id: "quick-clarity",
    testid: "service-quick-clarity",
    name: "Quick Clarity Sessions",
    tag: "Most Popular for First-Timers",
    best: "First-time clients, initial issue assessment, or those who need quick clarity and direction.",
    mode: "Phone call only",
    note: "Session 1 focuses on understanding your situation. Sessions 2–3 go deeper into guidance and action planning.",
    packages: [
      { id: "qc-single", label: "Single Session", dur: "20 min", amt: 399 },
      { id: "qc-extended", label: "Extended Session", dur: "40 min", amt: 699 },
      {
        id: "qc-pack3",
        label: "Quick Clarity Package (3)",
        sub: "Session 1: 20 min · Sessions 2 & 3: 30 min each",
        dur: "80 min",
        amt: 1299,
      },
    ],
  },
  {
    id: "individual",
    testid: "service-individual",
    name: "Individual Counselling",
    tag: "Recommended",
    recommended: true,
    best: "Individuals dealing with relationship concerns, communication issues, emotional patterns, breakups, self-awareness, or personal growth.",
    mode: "Google Meet / Zoom / WhatsApp Video",
    packages: [
      { id: "ind-single", label: "Single Session", dur: "30 min", amt: 699 },
      { id: "ind-starter", label: "Starter Package", dur: "3 sessions", amt: 1999 },
      { id: "ind-growth", label: "Growth Package", dur: "5 sessions", amt: 3199 },
      { id: "ind-transformation", label: "Transformation Package", dur: "8 sessions", amt: 4999 },
    ],
  },
  {
    id: "couple",
    testid: "service-couple",
    name: "Couple Counselling",
    tag: "For Both Partners",
    best: "Couples seeking to improve communication, resolve recurring conflicts, strengthen emotional connection, or navigate relationship transitions.",
    mode: "Google Meet / Zoom / WhatsApp Video",
    packages: [
      { id: "cpl-single", label: "Single Session", dur: "45 min", amt: 1199 },
      { id: "cpl-foundation", label: "Foundation Package", dur: "3 sessions", amt: 3399 },
      { id: "cpl-clarity", label: "Clarity Package", dur: "5 sessions", amt: 5499 },
    ],
  },
];

const rupee = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const ServiceCard = ({ svc }) => {
  const { goBook } = useNav();
  return (
    <div
      className={`service-card reveal ${svc.recommended ? "recommended" : ""}`}
      data-testid={svc.testid}
    >
      {svc.recommended && <div className="recommended-ribbon">★ Recommended</div>}
      <span className="service-tag">{svc.tag}</span>
      <h3 className="service-name">{svc.name}</h3>
      <p className="service-for"><strong>Best for:</strong> {svc.best}</p>
      <div className="service-mode">Mode: {svc.mode}</div>
      <div className="service-prices">
        {svc.packages.map((p) => (
          <div className="price-row" key={p.id}>
            <div className="label">
              {p.label}
              {p.sub && <small>{p.sub}</small>}
            </div>
            {p.dur && <div className="dur">{p.dur}</div>}
            <div className="amt">{rupee(p.amt)}</div>
          </div>
        ))}
      </div>
      {svc.note && <p className="service-note">{svc.note}</p>}
      <div className="service-card-footer">
        <button
          onClick={() => goBook()}
          className="btn btn-primary"
          style={{ width: "100%" }}
          data-testid={`${svc.testid}-book`}
        >
          Book this Session
        </button>
      </div>
    </div>
  );
};

const Services = () => (
  <section id="services" className="section section-alt">
    <div className="container">
      <div className="section-head reveal">
        <h2 className="section-title">Counselling Services</h2>
        <p className="section-subtitle">Choose the session that's right for you</p>
      </div>

      <div className="services-grid">
        {SERVICES.map((s) => <ServiceCard key={s.id} svc={s} />)}
      </div>

      <div className="services-cta-box reveal">
        All packages include a personalised written summary with infographics on your way forward —
        delivered after your sessions. Every session is completely confidential.
      </div>
    </div>
  </section>
);

/* =========================================================
   HOW IT WORKS
   ========================================================= */
const HowItWorks = () => {
  const steps = [
    {
      n: "01",
      icon: <Mail size={22} />,
      title: "Fill the Intake Form",
      desc: "Share basic details about yourself and what you're looking for. This helps Rupali understand your needs before you speak.",
    },
    {
      n: "02",
      icon: <IndianRupee size={22} />,
      title: "Confirm Payment",
      desc: "Pay securely via Cashfree (Cards / UPI / NetBanking) before your session is confirmed. Your slot is reserved only after payment.",
    },
    {
      n: "03",
      icon: <Pencil size={22} />,
      title: "Complete the Questionnaire",
      desc: "You'll receive a personalised pre-session questionnaire. Fill it honestly — Rupali reads every response before your session.",
    },
    {
      n: "04",
      icon: <Phone size={22} />,
      title: "Your Session",
      desc: "Join your session at the scheduled time via your chosen platform. Come as you are — no preparation needed beyond the questionnaire.",
    },
  ];

  return (
    <section id="how-it-works" className="section">
      <div className="container">
        <div className="section-head reveal">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">Simple, straightforward, and centred on you</p>
        </div>
        <div className="steps-grid">
          {steps.map((s, i) => (
            <div className="step-card reveal" key={i} data-testid={`step-${s.n}`}>
              <div className="step-num">{s.n}</div>
              <div className="step-icon">{s.icon}</div>
              <h3 className="step-title">{s.title}</h3>
              <p className="step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* =========================================================
   TESTIMONIALS
   ========================================================= */
const TESTIMONIALS = [
  {
    name: "Nikita Dureja",
    role: "Marriage Counselling",
    rating: 5,
    text:
      "Rupali didn't take sides. She helped us actually hear each other after years of silence. The written summary is something we still refer to.",
  },
  {
    name: "Shreya Singh",
    role: "Breakup & Healing",
    rating: 5,
    text:
      "She never rushed me, never made me feel small for crying. She helped me see patterns I'd carried since childhood. I finally understand myself.",
  },
  {
    name: "Sreeja",
    role: "Emotional Wellbeing",
    rating: 5,
    text:
      "Session was helpful because I shared my feelings in a better way. I felt I was not judged while speaking my heart out and Rupali heard me patiently. She gave me interventions, which I will follow. Overall I felt mentally lighter and confident.",
  },
  {
    name: "Hetal",
    role: "Self awareness",
    rating: 5,
    text:
      "There were a lot of emotions inside me, but due to this session I dived deep in myself and actually named everything. What triggers me, what drains my energy and what makes me feel anxious and sad.",
  },
  {
    name: "Ojasvi",
    role: "Clarity & Calm",
    rating: 4,
    text:
      "It helped me to relax and bring clarity on my thoughts. I felt calmer and more settled after the session.",
  },
  {
    name: "Kajal Yadav",
    role: "Premarital Counselling",
    rating: 4,
    text:
      "Booking couple sessions before our wedding was the best decision. She asked questions we had never thought to ask each other.",
  },
  {
    name: "Kavya Sharma",
    role: "Individual Counselling",
    rating: 5,
    text:
      "Her warmth made it easy to open up. She doesn't just give advice, she helps you find your own answers. The real sessions are even better than her videos.",
  },
  {
    name: "Ananya Chawla",
    role: "Anxiety & Calm",
    rating: 4,
    text:
      "I came in feeling overwhelmed by everything. Rupali helped me slow down and look at one thing at a time. I left with simple practices I actually use every day.",
  },
  {
    name: "Daksh Bundela",
    role: "Communication",
    rating: 5,
    text:
      "My wife and I were stuck in the same fight on repeat. Rupali pointed out our pattern so gently, neither of us felt blamed. We talk completely differently now.",
  },
  {
    name: "Reet Kaur",
    role: "Boundaries & Self respect",
    rating: 4,
    text:
      "I never realised how much I tried to please people until Rupali asked the right questions. She gave me the language, and the courage, to say no kindly.",
  },
  {
    name: "Nidhi Chauhan",
    role: "Family Dynamics",
    rating: 4,
    text:
      "As an Indian daughter dealing with conflicts in my husband's family, I needed someone who understood the culture. She gave me tools to set boundaries gently.",
  },
  {
    name: "Deepak R.",
    role: "Communication Issues",
    rating: 5,
    text:
      "I was skeptical about counselling. I thought it wasn't for men like me. She made me comfortable from the first call. We're communicating far better now.",
  },
  {
    name: "Vishak P.",
    role: "Self awareness & Growth",
    rating: 4,
    text:
      "The Growth Package helped me understand my attachment patterns, fears and strengths. The infographic summary felt like a personal roadmap.",
  },
];

const Stars = ({ count }) => (
  <div className="testi-stars" aria-label={`${count} star rating`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} className={i < count ? "star-on" : "star-off"}>★</span>
    ))}
  </div>
);

const Testimonials = () => {
  const [expanded, setExpanded] = useState(false);
  const initialCount = 4;
  const visible = expanded ? TESTIMONIALS : TESTIMONIALS.slice(0, initialCount);
  const hasMore = TESTIMONIALS.length > initialCount;

  return (
    <section id="testimonials" className="section section-alt">
      <div className="container">
        <div className="section-head reveal">
          <h2 className="section-title">What Clients Say</h2>
          <p className="section-subtitle">Real experiences, real change</p>
        </div>
        <div className="testimonials-grid">
          {visible.map((t, i) => (
            <div className="testi-card" key={i} data-testid={`testimonial-${i}`}>
              <span className="testi-quote">&ldquo;</span>
              <Stars count={t.rating} />
              <p className="testi-text">{t.text}</p>
              <h4 className="testi-name">{t.name}</h4>
              <p className="testi-role">{t.role}</p>
            </div>
          ))}
        </div>
        {hasMore && (
          <div className="testi-more-wrap">
            <button
              className="btn btn-outline testi-more-btn"
              onClick={() => setExpanded((v) => !v)}
              data-testid="testimonials-see-more"
            >
              {expanded ? "Show Less" : `See More (${TESTIMONIALS.length - initialCount})`}
              <ChevronUp
                size={16}
                style={{
                  transform: expanded ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

/* =========================================================
   NOTE
   ========================================================= */
const NoteSection = () => (
  <section className="section" style={{ padding: "60px 0" }}>
    <div className="container">
      <div className="note-card reveal" data-testid="important-note">
        <div className="note-icon"><Info size={26} /></div>
        <div>
          <h3>A Note Before You Book</h3>
          <p>
            <strong>Mind Matters</strong> Counselling &amp; Guidance Sessions are not a substitute
            for clinical therapy or psychiatric treatment. Rupali offers counselling and
            guidance based on her Masters in Psychology — she does not diagnose, prescribe, or treat
            clinical psychiatric conditions.
          </p>
          <p style={{ marginTop: 14 }}>
            <strong>If you are experiencing a mental health emergency, please reach out to:</strong>
          </p>
          <ul>
            <li>iCall (TISS): <strong>9152987821</strong></li>
            <li>Vandrevala Foundation: <strong>1860-2662-345</strong> (24×7)</li>
            <li>NIMHANS Helpline: <strong>080-46110007</strong></li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

/* =========================================================
   BOOK CTA
   ========================================================= */
const BookCTA = () => {
  const { goBook } = useNav();
  return (
    <section id="book" className="mm-book">
      <div className="mm-book-inner reveal">
        <h2>Ready to Begin?</h2>
        <p>
          Your first step toward clarity, healing, and growth starts here.
          Sessions are available in Hindi and English.
        </p>
        <div className="book-ctas">
          <button onClick={() => goBook()} className="btn btn-white" data-testid="book-cta-primary">Book a Session</button>
          <a
            href={URLS.terms}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-white-outline"
            data-testid="book-cta-terms"
          >
            Read Full Terms &amp; Conditions
          </a>
        </div>
        <div className="book-icons">
          <span><Lock size={16} /> 100% Confidential</span>
          <span><ClipboardList size={16} /> Pre-session questionnaire</span>
          <span><MessageCircle size={16} /> Hindi &amp; English</span>
        </div>
      </div>
    </section>
  );
};

/* =========================================================
   FOOTER
   ========================================================= */
const Footer = () => {
  const { goBook } = useNav();
  return (
    <footer className="mm-footer">
      <div className="container footer-grid">
        <div>
          <h3 className="footer-logo">Mind Matters</h3>
          <p className="footer-tag">with Rupali</p>
          <p className="footer-desc">
            A safe space for relationship counselling, emotional guidance, and mental wellbeing.
          </p>
          <div className="footer-socials">
            <a href={URLS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram"><Instagram size={18} /></a>
            <a href={URLS.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="footer-youtube"><Youtube size={18} /></a>
          </div>
        </div>
        <div className="footer-col">
          <h5>Quick Links</h5>
          <ul>
            <li><a href="#home">Home</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#services">Services</a></li>
            <li><a href="#how-it-works">How It Works</a></li>
            <li><a href="#testimonials">Testimonials</a></li>
            <li>
              <button
                onClick={() => goBook()}
                style={{ background: "none", border: "none", padding: 0, color: "#c8a7af", cursor: "pointer", fontSize: 14 }}
              >
                Book a Session
              </button>
            </li>
            <li>
              <a href={URLS.terms} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
            </li>
          </ul>
        </div>
        <div className="footer-col">
          <h5>Contact &amp; Info</h5>
          <p>Available in Hindi &amp; English</p>
          <p>Sessions via Google Meet, Zoom, WhatsApp</p>
          <p>Payment: Cashfree</p>
          <p>DM on Instagram for enquiries: <strong style={{ color: "#E8738A" }}>@mind_matters_rupali</strong></p>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>© 2026 Mind Matters | Rupali | All rights reserved.</span>
        <span>Designed for Mind Matters</span>
      </div>
    </footer>
  );
};

/* =========================================================
   SCROLL TO TOP
   ========================================================= */
const ScrollToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      className={`scroll-top ${show ? "show" : ""}`}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll to top"
      data-testid="scroll-to-top"
    >
      <ChevronUp size={22} />
    </button>
  );
};

/* =========================================================
   BOOKING PAGE
   ========================================================= */
const COUNTRIES = [
  "India", "United States", "United Kingdom", "Canada", "Australia",
  "United Arab Emirates", "Singapore", "Germany", "Other",
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman & Nicobar Islands", "Chandigarh", "Dadra & Nagar Haveli and Daman & Diu",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];

// 4 sessions per day
const TIME_SLOTS = [
  { value: "11:00", label: "11:00", hint: "11 AM" },
  { value: "12:00", label: "12:00", hint: "12 Noon" },
  { value: "17:00", label: "17:00", hint: "5 PM" },
  { value: "18:00", label: "18:00", hint: "6 PM" },
];

const fmtDateLong = (d) =>
  d
    ? d.toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

const toISODate = (d) => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const Field = ({ label, children, required }) => (
  <label className="bk-field">
    <span>
      {label} {required && <em style={{ color: "var(--mm-accent)", fontStyle: "normal" }}>*</em>}
    </span>
    {children}
  </label>
);

const BookingPage = ({ onContinue }) => {
  const { go } = useNav();
  const [selected, setSelected] = useState(null); // { service, package }
  const [form, setForm] = useState({
    name: "",
    country: "India",
    state: "",
    gender: "",
    email: "",
    phone: "",
    coupon: "",
  });
  const [sessionDate, setSessionDate] = useState(null); // Date
  const [sessionTime, setSessionTime] = useState("");
  const [bookedTimes, setBookedTimes] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [couponState, setCouponState] = useState({
    status: "idle", // idle | checking | valid | invalid
    discountPercent: 0,
    message: "",
    appliedCode: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Allowed booking window: earliest date is the day AFTER tomorrow (+2), then +45 days
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayAfterTomorrow = new Date(today);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
  const launchDate = new Date(2026, 4, 1); // May 1, 2026 (month is 0-indexed)
  const minDate = launchDate > dayAfterTomorrow ? launchDate : dayAfterTomorrow;
  const maxDate = new Date(minDate);
  maxDate.setDate(maxDate.getDate() + 45);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Fetch booked slots whenever the picked date changes
  useEffect(() => {
    if (!sessionDate) { setBookedTimes([]); return; }
    const iso = toISODate(sessionDate);
    setLoadingSlots(true);
    axios.get(`${API}/bookings/slots`, { params: { date: iso } })
      .then((res) => setBookedTimes(res.data?.booked || []))
      .catch(() => setBookedTimes([]))
      .finally(() => setLoadingSlots(false));
    // If currently selected time is now booked for the new date, clear it
    setSessionTime((prev) => prev);
  }, [sessionDate]);

  // If selected time gets blocked when bookedTimes loads, clear it
  useEffect(() => {
    if (sessionTime && bookedTimes.includes(sessionTime)) {
      setSessionTime("");
      toast.error("That slot just got booked. Please pick another time.");
    }
  }, [bookedTimes, sessionTime]);

  const applyCoupon = async () => {
    const code = form.coupon.trim();
    if (!code) {
      setCouponState({ status: "idle", discountPercent: 0, message: "", appliedCode: "" });
      return;
    }
    setCouponState((s) => ({ ...s, status: "checking", message: "" }));
    try {
      const res = await axios.get(`${API}/coupons/validate`, { params: { code } });
      const d = res.data || {};
      if (d.valid) {
        setCouponState({
          status: "valid",
          discountPercent: d.discount_percent || 0,
          message: d.message || "Coupon applied",
          appliedCode: d.code,
        });
        toast.success(d.message || "Coupon applied");
      } else {
        setCouponState({
          status: "invalid",
          discountPercent: 0,
          message: d.message || "Invalid coupon",
          appliedCode: "",
        });
        toast.error(d.message || "Invalid coupon");
      }
    } catch (err) {
      setCouponState({ status: "invalid", discountPercent: 0, message: "Could not validate coupon", appliedCode: "" });
      toast.error("Could not validate coupon");
    }
  };

  const baseAmount = selected?.pkg?.amt || 0;
  const discountPercent = couponState.status === "valid" ? couponState.discountPercent : 0;
  const discountValue = Math.round((baseAmount * discountPercent) / 100);
  const finalAmount = Math.max(0, baseAmount - discountValue);

  const validate = () => {
    const e = {};
    if (!selected) e.package = "Please choose a session";
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = "Valid mobile required";
    if (!form.country) e.country = "Required";
    if (form.country === "India" && !form.state) e.state = "Required";
    if (!form.gender) e.gender = "Required";
    if (!sessionDate) e.sessionDate = "Pick a date";
    if (!sessionTime) e.sessionTime = "Pick a time";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(
        !selected
          ? "Please select a session before continuing."
          : !sessionDate || !sessionTime
          ? "Please pick a date and time for your session."
          : "Please fix the highlighted fields."
      );
      return;
    }
    setSubmitting(true);
    const serviceLabel = `${selected.service.name} — ${selected.pkg.label}`;
    const isoDate = toISODate(sessionDate);
    try {
      const { data } = await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        country: form.country,
        state: form.state || null,
        gender: form.gender,
        coupon: couponState.status === "valid" ? couponState.appliedCode : null,
        preferred_service: serviceLabel,
        amount: baseAmount,
        discount_percent: discountPercent,
        final_amount: finalAmount,
        session_date: isoDate,
        session_time: sessionTime,
      });
      toast.success("Details saved. Proceed to payment.");
      onContinue({
        ...form,
        enquiryId: data.id,
        service: serviceLabel,
        amount: finalAmount,
        baseAmount,
        discountPercent,
        couponCode: couponState.status === "valid" ? couponState.appliedCode : null,
        sessionDate,
        sessionTime,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(msg);
      // refresh booked slots in case of conflict
      if (err?.response?.status === 409 && sessionDate) {
        const iso = toISODate(sessionDate);
        try {
          const res = await axios.get(`${API}/bookings/slots`, { params: { date: iso } });
          setBookedTimes(res.data?.booked || []);
          setSessionTime("");
        } catch (_) {}
      }
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bk-page">
      <div className="bk-container">
        <button
          className="bk-back"
          onClick={() => go("home")}
          data-testid="booking-back"
        >
          <ArrowLeft size={16} /> Back to home
        </button>

        <div className="bk-header">
          <div className="hero-label" style={{ marginBottom: 14 }}>Step 1 of 2 · Your Details</div>
          <h1 className="bk-title">Book Your Session</h1>
          <p className="bk-sub">
            Choose the session that works for you and share a few details so Rupali can prepare. All fields are kept strictly confidential.
          </p>
        </div>

        {/* Service / Package selector */}
        <div className="bk-picker" data-testid="booking-picker">
          <div className="bk-picker-label">
            Select your session <em style={{ color: "var(--mm-accent)", fontStyle: "normal" }}>*</em>
          </div>
          <div className="bk-picker-grid">
            {SERVICES.map((svc) => (
              <div className="bk-picker-col" key={svc.id}>
                <div className="bk-picker-col-head">
                  <span className="bk-picker-tag">{svc.tag}</span>
                  <h4>{svc.name}</h4>
                  <span className="bk-picker-mode">{svc.mode}</span>
                </div>
                <div className="bk-picker-options">
                  {svc.packages.map((p) => {
                    const isActive =
                      selected && selected.service.id === svc.id && selected.pkg.id === p.id;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => setSelected({ service: svc, pkg: p })}
                        className={`bk-picker-opt ${isActive ? "active" : ""}`}
                        data-testid={`bk-pkg-${p.id}`}
                      >
                        <div className="bk-picker-opt-main">
                          <div className="bk-picker-opt-label">{p.label}</div>
                          <div className="bk-picker-opt-dur">{p.dur}</div>
                        </div>
                        <div className="bk-picker-opt-price">{rupee(p.amt)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {errors.package && <p className="bk-picker-err">{errors.package}</p>}
        </div>

        <div className="bk-grid">
          <form className="bk-form" onSubmit={submit} data-testid="booking-form">
            <Field label="Full Name" required>
              <input
                data-testid="bk-name"
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. Nikita Sharma"
                className={errors.name ? "err" : ""}
              />
            </Field>

            <div className="bk-row">
              <Field label="Country" required>
                <select
                  data-testid="bk-country"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value, state: "" })}
                  className={errors.country ? "err" : ""}
                >
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="State" required={form.country === "India"}>
                {form.country === "India" ? (
                  <select
                    data-testid="bk-state"
                    value={form.state}
                    onChange={set("state")}
                    className={errors.state ? "err" : ""}
                  >
                    <option value="">Select state</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <input
                    data-testid="bk-state"
                    value={form.state}
                    onChange={set("state")}
                    placeholder="Your state / region"
                  />
                )}
              </Field>
            </div>

            <Field label="Gender" required>
              <select
                data-testid="bk-gender"
                value={form.gender}
                onChange={set("gender")}
                className={errors.gender ? "err" : ""}
              >
                <option value="">Select</option>
                {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>

            <div className="bk-row">
              <Field label="Email" required>
                <input
                  type="email"
                  data-testid="bk-email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={errors.email ? "err" : ""}
                />
              </Field>
              <Field label="Mobile Number" required>
                <input
                  data-testid="bk-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91 98xxxxxxxx"
                  className={errors.phone ? "err" : ""}
                />
              </Field>
            </div>

            <div className="bk-coupon-row">
              <Field label="Coupon Code (optional)">
                <div className="bk-coupon-wrap">
                  <input
                    data-testid="bk-coupon"
                    value={form.coupon}
                    onChange={(e) => {
                      set("coupon")(e);
                      if (couponState.status !== "idle")
                        setCouponState({ status: "idle", discountPercent: 0, message: "", appliedCode: "" });
                    }}
                    placeholder="Try HEAL10"
                    className={couponState.status === "valid" ? "ok" : couponState.status === "invalid" ? "err" : ""}
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={couponState.status === "checking" || !form.coupon.trim()}
                    className="bk-coupon-btn"
                    data-testid="bk-coupon-apply"
                  >
                    {couponState.status === "checking" ? "Checking…" : couponState.status === "valid" ? "Applied ✓" : "Apply"}
                  </button>
                </div>
              </Field>
              {couponState.message && (
                <p className={`bk-coupon-msg ${couponState.status === "valid" ? "ok" : "err"}`}>
                  {couponState.message}
                </p>
              )}
            </div>

            {/* Schedule — date + time */}
            <div className="bk-schedule" data-testid="bk-schedule">
              <div className="bk-schedule-head">
                <CalendarDays size={18} />
                <div>
                  <h4>Choose a Date &amp; Time</h4>
                  <p>Pick any slot within the next 45 days. Rupali will confirm via email.</p>
                </div>
              </div>

              <div className="bk-schedule-grid">
                <div className={`bk-cal-wrap ${errors.sessionDate ? "err-wrap" : ""}`}>
                  <DayPicker
                    mode="single"
                    selected={sessionDate}
                    onSelect={setSessionDate}
                    disabled={[{ before: minDate }, { after: maxDate }]}
                    fromDate={minDate}
                    toDate={maxDate}
                    defaultMonth={minDate}
                    weekStartsOn={1}
                    showOutsideDays={false}
                    data-testid="bk-calendar"
                  />
                  {errors.sessionDate && <p className="bk-inline-err">{errors.sessionDate}</p>}
                </div>

                <div className={`bk-slots ${errors.sessionTime ? "err-wrap" : ""}`}>
                  <div className="bk-slots-head">
                    <Clock size={14} />
                    <span>
                      {sessionDate ? fmtDateLong(sessionDate) : "Select a date to pick a time"}
                      {loadingSlots && " · checking availability…"}
                    </span>
                  </div>
                  <div className="bk-slots-grid">
                    {TIME_SLOTS.map((t) => {
                      const isBooked = bookedTimes.includes(t.value);
                      const isActive = sessionTime === t.value;
                      return (
                        <button
                          type="button"
                          key={t.value}
                          onClick={() => !isBooked && setSessionTime(t.value)}
                          className={`bk-slot ${isActive ? "active" : ""} ${isBooked ? "booked" : ""}`}
                          disabled={!sessionDate || isBooked}
                          data-testid={`bk-slot-${t.value}`}
                          aria-disabled={isBooked}
                          title={isBooked ? "Already booked" : ""}
                        >
                          <span className="bk-slot-label">{t.label}</span>
                          <span className="bk-slot-hint">{isBooked ? "Booked" : t.hint}</span>
                        </button>
                      );
                    })}
                  </div>
                  {errors.sessionTime && <p className="bk-inline-err">{errors.sessionTime}</p>}
                  <p className="bk-slots-note">All times in IST · Sessions are 1-on-1 &amp; confidential</p>
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: "100%", marginTop: 8 }}
              disabled={submitting}
              data-testid="bk-submit"
            >
              {submitting ? <><Loader2 size={16} className="spin" /> Saving…</> : "Continue to Payment"}
            </button>

            <p className="bk-note">
              By continuing, you agree to our{" "}
              <a href={URLS.terms} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>.
            </p>
          </form>

          <aside className="bk-summary">
            <div className="bk-summary-tag">Booking Summary</div>
            {selected ? (
              <>
                <h3>{selected.service.name} — {selected.pkg.label}</h3>
                {discountPercent > 0 ? (
                  <div className="bk-amount-block">
                    <div className="bk-amount-strike">{rupee(baseAmount)}</div>
                    <div className="bk-amount">{rupee(finalAmount)}</div>
                    <div className="bk-discount-chip">
                      {discountPercent}% off · saved {rupee(discountValue)}
                    </div>
                  </div>
                ) : (
                  <div className="bk-amount">{rupee(baseAmount)}</div>
                )}
              </>
            ) : (
              <>
                <h3 style={{ color: "var(--mm-muted)" }}>No session selected yet</h3>
                <p style={{ fontSize: 13, color: "var(--mm-muted)", margin: "12px 0 20px 0" }}>
                  Pick a session above to see pricing and continue.
                </p>
              </>
            )}

            {(sessionDate || sessionTime) && (
              <div className="bk-summary-schedule">
                <div className="bk-summary-schedule-row">
                  <CalendarDays size={14} />
                  <span>{sessionDate ? fmtDateLong(sessionDate) : "Date not picked"}</span>
                </div>
                <div className="bk-summary-schedule-row">
                  <Clock size={14} />
                  <span>
                    {sessionTime
                      ? `${sessionTime} · ${
                          TIME_SLOTS.find((s) => s.value === sessionTime)?.hint || ""
                        }`
                      : "Time not picked"}
                  </span>
                </div>
              </div>
            )}

            <ul className="bk-benefits">
              <li><CheckCircle2 size={15} /> Personalised written summary</li>
              <li><CheckCircle2 size={15} /> 100% confidential</li>
              <li><CheckCircle2 size={15} /> Hindi &amp; English</li>
              <li><CheckCircle2 size={15} /> Questionnaire before session</li>
            </ul>
            <p className="bk-help">
              Need help choosing? DM <strong>@mind_matters_rupali</strong> on Instagram.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   PAYMENT PAGE (Cashfree Checkout + Questionnaire link)
   ========================================================= */
const PaymentPage = ({ booking }) => {
  const { go } = useNav();
  const amount = booking?.amount || 0;
  const [cfState, setCfState] = useState("idle"); // idle | loading | failed

  const payWithCashfree = async () => {
    if (!booking?.enquiryId) {
      toast.error("Missing booking reference. Please start your booking again.");
      return;
    }
    setCfState("loading");
    try {
      const { data } = await axios.post(`${API}/payments/cashfree/order`, {
        enquiry_id: booking.enquiryId,
      });
      const cashfree = await loadCashfree({ mode: data.environment });
      // Full-page redirect (Cashfree's recommended mode) — required so UPI
      // apps like PhonePe/GPay can be launched directly via intent instead
      // of falling back to a QR code, which happens inside an iframe/modal.
      await cashfree.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_self",
      });
      // Control only returns here if checkout failed to even start (e.g. SDK
      // error); on a real redirect the browser navigates away before this runs.
    } catch (err) {
      setCfState("failed");
      toast.error(err?.response?.data?.detail || "Could not start Cashfree checkout. Please try again.");
    }
  };

  return (
    <div className="bk-page">
      <div className="bk-container">
        <button className="bk-back" onClick={() => go("home")} data-testid="payment-back">
          <ArrowLeft size={16} /> Back to home
        </button>

        <div className="bk-header">
          <div className="hero-label" style={{ marginBottom: 14, color: "var(--mm-accent)" }}>
            Step 2 of 2 · Payment &amp; Questionnaire
          </div>
          <h1 className="bk-title">You're almost there, {booking?.name?.split(" ")[0] || "there"}.</h1>
          <p className="bk-sub">
            Complete your secure payment below and fill the pre-session questionnaire. Your slot is reserved once both steps are complete.
          </p>

          {(booking?.sessionDate || booking?.sessionTime) && (
            <div className="pay-schedule-chip" data-testid="payment-schedule-chip">
              <span><CalendarDays size={14} /> {booking?.sessionDate ? fmtDateLong(booking.sessionDate) : ""}</span>
              <span className="dot">·</span>
              <span>
                <Clock size={14} /> {booking?.sessionTime} {booking?.sessionTime && `· ${TIME_SLOTS.find(s => s.value === booking.sessionTime)?.hint || ""}`}
              </span>
            </div>
          )}
        </div>

        <div className="pay-grid">
          <div className="pay-card pay-card-checkout" data-testid="payment-qr-card">
            <div className="pay-step">Step 1 — Secure Payment</div>
            <div className="pay-cf-amount-row">
              <div className="pay-amount">₹ {amount.toLocaleString("en-IN")}</div>
              <div className="pay-cf-lock"><Lock size={14} /> Secured</div>
            </div>
            {booking?.discountPercent > 0 && (
              <div className="pay-discount-chip">
                {booking.couponCode} applied · {booking.discountPercent}% off ·
                was {rupee(booking.baseAmount)}
              </div>
            )}
            <div className="pay-service">{booking?.service}</div>

            <div className="pay-cf-trust-row">
              <span><CreditCard size={15} /> Cards · UPI · NetBanking</span>
              <span><Zap size={15} /> Instant confirmation</span>
              <span><Shield size={15} /> 256-bit encrypted</span>
            </div>

            <button
              type="button"
              onClick={payWithCashfree}
              disabled={cfState === "loading"}
              className="btn btn-primary pay-cf-btn"
              data-testid="cashfree-pay-btn"
            >
              {cfState === "loading" ? (
                <><Loader2 size={17} className="spin" /> Opening secure checkout…</>
              ) : (
                <><Lock size={16} /> Pay ₹{amount.toLocaleString("en-IN")} Securely</>
              )}
            </button>

            {cfState === "failed" && (
              <p className="pay-cf-note pay-cf-err" data-testid="cashfree-status-failed">
                Payment wasn't completed. Please try again.
              </p>
            )}

            <div className="pay-cf-powered">Payments powered by Cashfree Payments</div>
          </div>

          <div className="pay-card pay-card-alt" data-testid="payment-questionnaire-card">
            <div className="pay-step">Step 2 — Complete the Questionnaire</div>
            <h3 className="pay-qtitle">Pre-session questionnaire</h3>
            <p className="pay-qtext">
              This is required before your session. Rupali reads every response carefully to make your session more meaningful.
            </p>
            <a
              href={URLS.questionnaire}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ width: "100%" }}
              data-testid="open-questionnaire"
            >
              Open Questionnaire <ExternalLink size={14} />
            </a>

            <div className="pay-divider" />

            <p className="pay-tnc">
              By proceeding, you confirm you have read and agree to the{" "}
              <a href={URLS.terms} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>.
            </p>

            <a
              href={URLS.terms}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
              style={{ width: "100%", marginTop: 8 }}
              data-testid="payment-terms-link"
            >
              Read Full Terms &amp; Conditions
            </a>
          </div>
        </div>

        <div className="pay-footer">
          <p>Questions? DM <strong>@mind_matters_rupali</strong> on Instagram and we'll get back within a few hours.</p>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   ADMIN PAGE
   ========================================================= */
const AdminPage = () => {
  const { go } = useNav();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("mm_admin_pw", password);
      setAuthed(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid password");
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    setError("");
    try {
      const pw = sessionStorage.getItem("mm_admin_pw") || password;
      const res = await axios.get(`${API}/enquiries`, {
        headers: { "X-Admin-Password": pw },
      });
      setBookings(res.data || []);
    } catch (err) {
      setError("Could not load bookings");
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("mm_admin_pw");
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("mm_admin_pw");
    if (saved) {
      setPassword(saved);
      setAuthed(true);
    }
  }, []);

  useEffect(() => {
    if (authed) fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const fmtAmt = (b) => {
    if (b.final_amount && b.final_amount !== b.amount) {
      return `₹${b.final_amount} (was ₹${b.amount}, ${b.discount_percent}% off)`;
    }
    return `₹${b.amount || 0}`;
  };

  return (
    <div className="bk-page">
      <div className="bk-container" style={{ maxWidth: 1400 }}>
        <button className="bk-back" onClick={() => go("home")} data-testid="admin-back">
          <ArrowLeft size={16} /> Back to home
        </button>

        {!authed ? (
          <div className="admin-login" data-testid="admin-login">
            <h1 className="bk-title" style={{ fontSize: 36 }}>Admin Login</h1>
            <p className="bk-sub">Mind Matters — bookings dashboard</p>
            <form onSubmit={login} className="admin-login-form">
              <Field label="Password" required>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  data-testid="admin-password"
                  autoFocus
                />
              </Field>
              {error && <p className="bk-coupon-msg err">{error}</p>}
              <button type="submit" className="btn btn-primary" style={{ width: "100%" }} data-testid="admin-login-btn">
                Sign In
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="admin-head">
              <div>
                <h1 className="bk-title" style={{ fontSize: 36, marginBottom: 6 }}>All Bookings</h1>
                <p className="bk-sub" style={{ margin: 0 }}>{bookings.length} total</p>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={fetchBookings} className="btn btn-outline btn-sm" disabled={loading}>
                  {loading ? <Loader2 size={14} className="spin" /> : "Refresh"}
                </button>
                <button
                  onClick={() => { sessionStorage.removeItem("mm_admin_pw"); setAuthed(false); setBookings([]); }}
                  className="btn btn-outline btn-sm"
                >
                  Sign Out
                </button>
              </div>
            </div>

            {error && <p className="bk-coupon-msg err">{error}</p>}

            <div className="admin-table-wrap">
              <table className="admin-table" data-testid="admin-bookings-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Service</th>
                    <th>Amount</th>
                    <th>Payment</th>
                    <th>Coupon</th>
                    <th>Country / State</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && !loading && (
                    <tr><td colSpan={10} style={{ textAlign: "center", padding: 24, color: "var(--mm-muted)" }}>No bookings yet</td></tr>
                  )}
                  {bookings.map((b) => (
                    <tr key={b.id}>
                      <td>{b.session_date || "—"}</td>
                      <td><strong>{b.session_time || "—"}</strong></td>
                      <td>{b.name}</td>
                      <td>
                        <div>{b.email}</div>
                        <div style={{ fontSize: 12, color: "var(--mm-muted)" }}>{b.phone}</div>
                      </td>
                      <td style={{ maxWidth: 220 }}>{b.preferred_service}</td>
                      <td>{fmtAmt(b)}</td>
                      <td>
                        {b.payment_method === "cashfree" ? (
                          <span style={{ color: b.payment_status === "paid" ? "#2a9d52" : b.payment_status === "failed" ? "#d9455a" : "var(--mm-muted)", fontWeight: 500 }}>
                            Cashfree · {b.payment_status === "paid" ? "Paid" : b.payment_status === "failed" ? "Failed" : "Pending"}
                          </span>
                        ) : (
                          <span style={{ color: "var(--mm-muted)" }}>Not paid yet</span>
                        )}
                      </td>
                      <td>{b.coupon || "—"}</td>
                      <td>{[b.state, b.country].filter(Boolean).join(", ") || "—"}</td>
                      <td style={{ fontSize: 12, color: "var(--mm-muted)" }}>
                        {b.created_at ? new Date(b.created_at).toLocaleString("en-IN") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


/* =========================================================
   APP
   ========================================================= */
function App() {
  const initialPage = typeof window !== "undefined" && window.location.hash === "#admin" ? "admin" : "home";
  const [page, setPage] = useState(initialPage); // 'home' | 'book' | 'payment' | 'admin'
  const [booking, setBooking] = useState(null);
  const cfReturnHandled = useRef(null);

  const go = (p) => {
    setPage(p);
    if (p === "admin") window.location.hash = "admin";
    else if (window.location.hash === "#admin") window.location.hash = "";
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  const goBook = () => {
    setPage("book");
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  const goAnchor = (hash) => {
    if (page !== "home") {
      setPage("home");
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === "#admin") setPage("admin");
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Cashfree redirects back here (full-page redirect) after the hosted
  // checkout completes — this is the primary way we learn the outcome.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cfOrderId = params.get("cf_order_id");
    if (!cfOrderId || cfReturnHandled.current === cfOrderId) return;
    cfReturnHandled.current = cfOrderId;
    axios
      .get(`${API}/payments/cashfree/status/${cfOrderId}`)
      .then(({ data }) => {
        if (data?.payment_status === "paid") {
          toast.success("Payment confirmed via Cashfree! Check your email for your session details.", { id: "cf-return", duration: 8000 });
        } else if (data?.cashfree_status === "ACTIVE") {
          toast.error("Payment wasn't completed. You can try again whenever you're ready.", { id: "cf-return", duration: 8000 });
        } else {
          toast.error("We couldn't confirm your payment. If money was deducted, please message us on WhatsApp/Instagram and we'll verify it.", { id: "cf-return", duration: 10000 });
        }
      })
      .catch(() => {
        toast.error("We couldn't verify your payment. If money was deducted, please message us on WhatsApp/Instagram and we'll verify it.", { id: "cf-return", duration: 10000 });
      })
      .finally(() => {
        params.delete("cf_order_id");
        const qs = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
      });
  }, []);

  useEffect(() => {
    if (page !== "home") return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    const els = document.querySelectorAll(".reveal");
    els.forEach((el) => observer.observe(el));

    const raf = requestAnimationFrame(() => {
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.classList.add("in-view");
        }
      });
    });

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [page]);

  return (
    <NavCtx.Provider value={{ go, goBook, goAnchor }}>
      <Toaster position="top-center" richColors closeButton />
      <div className="App">
        <Navigation />
        {page === "home" && (
          <>
            <Hero />
            <About />
            <Services />
            <HowItWorks />
            <Testimonials />
            <NoteSection />
            <BookCTA />
            <Footer />
            <ScrollToTop />
          </>
        )}
        {page === "book" && (
          <BookingPage
            onContinue={(data) => {
              setBooking(data);
              setPage("payment");
              window.scrollTo({ top: 0, behavior: "instant" });
            }}
          />
        )}
        {page === "payment" && <PaymentPage booking={booking} />}
        {page === "admin" && <AdminPage />}
      </div>
    </NavCtx.Provider>
  );
}

export default App;
